// Mouse-position broadcaster. On each pointermove inside the matrix,
// figures out which quadrant the pointer is over and emits coordinates
// normalized to *that quadrant's* bounding box. This gives consistent
// "I'm hovering this part of Q1" semantics across browser windows of
// different sizes, even when the matrix has very different aspect
// ratios on each end.
//
// Throttled to ~20 Hz; emits a final `null` after 10 s of stillness.
// Touch devices are skipped — the design explicitly drops cursor
// presence on touch.
//
// Sending is also gated on whether anyone would see the update: when
// we're the only client in the room, or our tab is hidden, the
// pointermove handler updates local bookkeeping but skips the network
// call. On the transition back to "someone is watching", we flush the
// last known position so the new peer sees us immediately.

import type { CursorPos } from "../generated/CursorPos";
import type { Quadrant } from "../generated/Quadrant";
import { local, sendCursor, subscribe } from "../state";

const THROTTLE_MS = 50;
const MIN_DELTA = 0.005;
const IDLE_MS = 10_000;

interface LastSent {
  quadrant: Quadrant | null;
  x: number;
  y: number;
  t: number;
}

let lastSent: LastSent = { quadrant: null, x: -1, y: -1, t: 0 };
let lastKnownPos: CursorPos | null = null;
let idleTimer: ReturnType<typeof setTimeout> | null = null;
let attached: HTMLElement | null = null;
let unsubscribeState: (() => void) | null = null;
let sendingEnabled = false;

export function mountCursorTracker(matrix: HTMLElement): void {
  unmountCursorTracker();
  attached = matrix;
  matrix.addEventListener("pointermove", onPointerMove);
  matrix.addEventListener("pointerleave", onLeave);
  document.addEventListener("visibilitychange", onVisibilityChange);
  unsubscribeState = subscribe((kind) => {
    if (kind === "cursor-only") return;
    reevaluateGating();
  });
  reevaluateGating();
}

export function unmountCursorTracker(): void {
  if (attached) {
    attached.removeEventListener("pointermove", onPointerMove);
    attached.removeEventListener("pointerleave", onLeave);
  }
  attached = null;
  document.removeEventListener("visibilitychange", onVisibilityChange);
  if (unsubscribeState) {
    unsubscribeState();
    unsubscribeState = null;
  }
  if (idleTimer !== null) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
  lastSent = { quadrant: null, x: -1, y: -1, t: 0 };
  lastKnownPos = null;
  sendingEnabled = false;
}

function hasRemoteViewer(): boolean {
  const meId = local.me?.clientId;
  for (const c of local.presence.values()) {
    if (c.clientId !== meId) return true;
  }
  return false;
}

function shouldSend(): boolean {
  return !document.hidden && hasRemoteViewer();
}

function reevaluateGating(): void {
  const next = shouldSend();
  if (next === sendingEnabled) return;
  sendingEnabled = next;
  if (!next) {
    // Just went suppressed — clear whatever peers last saw.
    if (lastSent.quadrant !== null) {
      sendCursor(null);
      lastSent = { quadrant: null, x: -1, y: -1, t: 0 };
    }
    if (idleTimer !== null) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
  } else if (lastKnownPos) {
    // Just resumed — flush the most recent position so the new viewer
    // sees us without waiting for the next pointermove.
    sendCursor(lastKnownPos);
    lastSent = {
      quadrant: lastKnownPos.quadrant,
      x: lastKnownPos.x,
      y: lastKnownPos.y,
      t: performance.now(),
    };
    scheduleIdleClear();
  }
}

function onVisibilityChange(): void {
  reevaluateGating();
}

function onPointerMove(e: PointerEvent): void {
  if (e.pointerType === "touch") return;
  const target = e.target;
  if (!(target instanceof Element)) return;
  const quadrantEl = target.closest<HTMLElement>("[data-quadrant]");
  if (!quadrantEl) {
    // Pointer is over the toolbar, the cursor overlay, or some other
    // child of the page that isn't part of any quadrant — clear.
    lastKnownPos = null;
    clearCursor();
    return;
  }
  const quadrant = quadrantEl.dataset.quadrant as Quadrant | undefined;
  if (!quadrant) return;
  const rect = quadrantEl.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return;
  const x = clamp01((e.clientX - rect.left) / rect.width);
  const y = clamp01((e.clientY - rect.top) / rect.height);
  lastKnownPos = { quadrant, x, y };
  if (!sendingEnabled) return;
  const now = performance.now();
  if (now - lastSent.t < THROTTLE_MS) return;
  if (
    lastSent.quadrant === quadrant &&
    Math.abs(x - lastSent.x) < MIN_DELTA &&
    Math.abs(y - lastSent.y) < MIN_DELTA
  )
    return;
  lastSent = { quadrant, x, y, t: now };
  sendCursor(lastKnownPos);
  scheduleIdleClear();
}

function onLeave(): void {
  lastKnownPos = null;
  clearCursor();
}

function clearCursor(): void {
  if (lastSent.quadrant === null && lastSent.x < 0) return;
  sendCursor(null);
  lastSent = { quadrant: null, x: -1, y: -1, t: 0 };
  if (idleTimer !== null) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
}

function scheduleIdleClear(): void {
  if (idleTimer !== null) clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    sendCursor(null);
    lastSent = { quadrant: null, x: -1, y: -1, t: 0 };
    idleTimer = null;
  }, IDLE_MS);
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
